import { COPILOT_CONTROL_EVENTS } from "campaign-builder/analytics/cbAnalytics";
import { sanitizeControlEvent } from "./controlEventSanitizer.mjs";

export { sanitizeControlEvent };

// Copilot control-mode telemetry (WP-H3 of the Copilot control-mode plan).
//
// The library (`@oppizi-ecosystem/copilot`) only knows WHAT happened during a
// control session — it emits a transport-agnostic `ControlEventName` (see the
// library's `control/events.ts`: requested | granted | declined | stopped |
// paused | resumed | action | refused | timeout | no_effect | error |
// connection_lost | reconnected | takeover) via the `onEvent` option passed
// to `useUiControl`. This module is the host's translation of that into the
// builder's own PostHog taxonomy (`COPILOT_CONTROL_EVENTS`).
//
// `reconnected` has no counterpart in COPILOT_CONTROL_EVENTS (the spec tracks
// the *loss*, not the recovery) — it is intentionally left unmapped and
// dropped, same as any name the library might add later that this host does
// not yet know about. Nothing here throws on an unknown name.

const EVENT_NAME_TO_CB_EVENT = {
  requested: COPILOT_CONTROL_EVENTS.REQUESTED,
  granted: COPILOT_CONTROL_EVENTS.GRANTED,
  declined: COPILOT_CONTROL_EVENTS.DECLINED,
  stopped: COPILOT_CONTROL_EVENTS.STOPPED,
  paused: COPILOT_CONTROL_EVENTS.PAUSED,
  resumed: COPILOT_CONTROL_EVENTS.RESUMED,
  action: COPILOT_CONTROL_EVENTS.ACTION,
  refused: COPILOT_CONTROL_EVENTS.REFUSED,
  timeout: COPILOT_CONTROL_EVENTS.TIMEOUT,
  no_effect: COPILOT_CONTROL_EVENTS.NO_EFFECT,
  error: COPILOT_CONTROL_EVENTS.ERROR,
  connection_lost: COPILOT_CONTROL_EVENTS.CONNECTION_LOST,
  takeover: COPILOT_CONTROL_EVENTS.TAKEOVER,
};

const currentPath = () => (typeof window !== "undefined" ? window.location.pathname : "");

/**
 * Build the host-side `onEvent` handler passed to `useUiControl`. Every
 * library `ControlEventName` this host recognizes is mapped to one
 * `COPILOT_CONTROL_EVENTS` entry and captured once, with props sanitized
 * through the allowlist above. Unknown/unmapped event names (e.g. the
 * library's `reconnected`) are silently ignored — never thrown.
 *
 * @param {{
 *   posthog: { capture: (name: string, props?: object) => void },
 *   getConversationId: () => string | null | undefined,
 *   getCampaignId?: () => string | number | null | undefined,
 *   getStep?: () => string | null | undefined,
 * }} deps
 */
export const createControlTelemetry =
  ({ posthog, getConversationId, getCampaignId, getStep }) =>
  (event) => {
    if (!event || !event.name) return;
    const cbEvent = EVENT_NAME_TO_CB_EVENT[event.name];
    if (!cbEvent) return;

    const conversationId = getConversationId?.();
    if (!conversationId) return; // nothing to correlate the event to — drop it rather than send a partial row

    const raw = {
      conversation_id: conversationId,
      campaign_id: getCampaignId?.() ?? undefined,
      host: "builder",
      grant_scope: event.scope,
      action: event.action,
      status: event.status,
      step: getStep?.(),
      actions_used: event.actionsUsed ?? event.actions_used,
      duration_ms: event.duration_ms,
      reason: event.reason,
      path: currentPath(),
    };

    try {
      posthog.capture(cbEvent, sanitizeControlEvent(raw));
    } catch (error) {
      // Analytics must never break the control loop.
      console.error("copilot control telemetry: capture failed:", error);
    }
  };
